import { createContext, useContext, useState, useEffect, useMemo, useCallback } from "react";

const LanguageContext = createContext();

const SUPPORTED_LANGUAGES = [
  { code: "da", name: "Dansk", flag: "🇩🇰" },
  { code: "sv", name: "Svenska", flag: "🇸🇪" },
  { code: "no", name: "Norsk", flag: "🇳🇴" },
  { code: "en", name: "English", flag: "🇬🇧" },
  { code: "de", name: "Deutsch", flag: "🇩🇪" },
  { code: "fr", name: "Français", flag: "🇫🇷" },
  { code: "es", name: "Español", flag: "🇪🇸" },
  { code: "it", name: "Italiano", flag: "🇮🇹" },
];

const COUNTRY_TO_LANGUAGE = {
  DK: "da", SE: "sv", NO: "no",
  DE: "de", AT: "de", CH: "de",
  FR: "fr", MC: "fr", BE: "fr",
  ES: "es", IT: "it",
  GB: "en", US: "en", AU: "en", NZ: "en", CA: "en", IE: "en",
};

const COUNTRY_TO_CURRENCY = {
  DK: "DKK", SE: "SEK", NO: "NOK",
  GB: "GBP", US: "USD", CA: "USD", AU: "USD",
};

function detectLanguageFromBrowser() {
  const browserLang = (navigator.language || "en").split("-")[0].toLowerCase();
  const supported = SUPPORTED_LANGUAGES.map((l) => l.code);
  if (supported.includes(browserLang)) return browserLang;
  if (browserLang === "nb" || browserLang === "nn") return "no";
  return "en";
}

export function LanguageProvider({ children }) {
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem("ga_lang") || detectLanguageFromBrowser();
  });
  const [currency, setCurrency] = useState(() => {
    return localStorage.getItem("ga_currency") || "EUR";
  });
  const [detectionDone, setDetectionDone] = useState(false);

  // IP-based country detection for currency (and language if no stored preference)
  useEffect(() => {
    const stored = localStorage.getItem("ga_lang");
    if (stored) {
      setDetectionDone(true);
      return;
    }

    const detectCountry = async () => {
      try {
        const res = await fetch("https://ipapi.co/json/", { signal: AbortSignal.timeout(3000) });
        const data = await res.json();
        const country = data.country_code;

        if (country) {
          const detectedLang = COUNTRY_TO_LANGUAGE[country];
          if (detectedLang) setLanguage(detectedLang);

          const detectedCurrency = COUNTRY_TO_CURRENCY[country] || "EUR";
          setCurrency(detectedCurrency);
          localStorage.setItem("ga_currency", detectedCurrency);
        }
      } catch (error) {
        console.error("IP geolocation failed:", error);
      } finally {
        setDetectionDone(true);
      }
    };

    detectCountry();
  }, []);

  const changeLanguage = useCallback((lang) => {
    setLanguage(lang);
    localStorage.setItem("ga_lang", lang);
  }, []);

  const changeCurrency = useCallback((cur) => {
    setCurrency(cur);
    localStorage.setItem("ga_currency", cur);
  }, []);

  const contextValue = useMemo(() => ({
    language,
    currency,
    changeLanguage,
    changeCurrency,
    detectionDone,
    languages: SUPPORTED_LANGUAGES,
  }), [language, currency, changeLanguage, changeCurrency, detectionDone]);

  return (
    <LanguageContext.Provider value={contextValue}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLanguage must be used within LanguageProvider");
  return ctx;
}

export function useT(translations) {
  const { language } = useLanguage();
  return translations[language] || translations.en;
}
